import { toast } from "sonner";
import { getApiErrorType } from "@/lib/api-error";
import { isRetryableNetworkError } from "@/lib/network-error";
import type { ErrorMessageType } from "@/lib/server/errors/error-message";

export type QueryFailurePresentation =
  | { kind: "render" }
  | { kind: "not_found" }
  | { kind: "access_denied" }
  | { kind: "fatal"; error: Error };

const ACCESS_DENIED_TYPES = new Set<ErrorMessageType>([
  "UNAUTHORIZED_REQUEST_FOR_USER_TYPE",
  "INSUFFICIENT_PERMISSIONS",
  "CONVERSATION_UNAUTHORIZED",
  "UNAUTHORIZED_ADMIN_ACCESS",
]);

function isNotFoundType(type: ErrorMessageType): boolean {
  return type.endsWith("_NOT_FOUND");
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return new Error((error as { message: string }).message);
  }

  return new Error(String(error));
}

export function resolveQueryFailure(
  error: unknown,
  data: unknown
): QueryFailurePresentation | null {
  if (!error) {
    return null;
  }

  if (data != null) {
    return { kind: "render" };
  }

  const type = getApiErrorType(error);

  if (type && isNotFoundType(type)) {
    return { kind: "not_found" };
  }

  if (type && ACCESS_DENIED_TYPES.has(type)) {
    return { kind: "access_denied" };
  }

  return { kind: "fatal", error: toError(error) };
}

export function getBackgroundQueryErrorToast(error: unknown): {
  title: string;
  description?: string;
} | null {
  if (!error) {
    return null;
  }

  if (isRetryableNetworkError(error)) {
    return {
      title: "Connection issue",
      description: "Check your connection and try again.",
    };
  }

  const message = error instanceof Error ? error.message : String(error);

  return {
    title: "Uh oh! Something went wrong.",
    description: message,
  };
}

export function getMutationNetworkErrorToast(
  error: unknown,
  title: string
): { title: string; description: string } | null {
  if (!isRetryableNetworkError(error)) {
    return null;
  }

  return {
    title,
    description: "Check your connection and try again.",
  };
}

export function toastMutationNetworkError(
  error: unknown,
  title: string
): boolean {
  const content = getMutationNetworkErrorToast(error, title);
  if (!content) {
    return false;
  }

  toast.error(content.title, { description: content.description });
  return true;
}
